<script setup>
import { ref, reactive, watch, onMounted, computed } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost, obterCorDinamica, obterBadgeNeutro } = useUI()

const lista = ref([])
const isLoading = ref(false)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({
  lstTipoTributo: '',
  lstMomentoGeracao: '',
  situacao: '',
  dataInicioDe: '',
  dataInicioAte: ''
})

const listas = reactive({})

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const emit = defineEmits(['editar-item', 'adicionar-item'])

const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('TRIBUTO_CONFIGURACAO_COMPLETO'))
})

const colunas = [
  { field: 'idTributoConfiguracao', label: 'ID', width: 55, sortable: true, slotName: 'id' },
  { field: 'lstTipoTributo', label: 'Tipo de Tributo', width: 130, sortable: true, slotName: 'tipoTributo' },
  { field: 'lstMomentoGeracao', label: 'Momento de Geração', width: 130, sortable: true, slotName: 'momentoGeracao' },
  { field: 'dataInicioVigencia', label: 'Início Vigência', width: 90, sortable: true, slotName: 'dataInicio' },
  { field: 'dataFimVigencia', label: 'Fim Vigência', width: 90, sortable: true, slotName: 'dataFim' },
  { field: 'situacao', label: 'Situação', width: 120, align: 'center', sortable: false, slotName: 'situacao' },
  { field: 'tsCriacao', label: 'Data Criação', width: 135, sortable: true, slotName: 'tsCriacao' },
  { field: 'tsModificacao', label: 'Data Modif.', width: 135, sortable: true, slotName: 'tsModificacao' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstTipoTributo: (row) => getTextoLista('LstTipoTributo', row.lstTipoTributo) || row.lstTipoTributo || '',
    lstMomentoGeracao: (row) => getTextoLista('LstMomentoGeracao', row.lstMomentoGeracao) || row.lstMomentoGeracao || ''
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

async function carregarListas () {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['TributoConfiguracao'])
    Object.assign(listas, result)
  } catch (e) { console.warn('Erro ao carregar listas:', e) }
}

function getTextoLista (campo, idItem) {
  if (!idItem) return null
  const listaCampo = listas[campo]
  if (!Array.isArray(listaCampo)) return null
  const obj = listaCampo.find(s => s.idListaSistemaItem === idItem || s.idListaClienteItem === idItem)
  return obj ? obj.textoLista : null
}

function getTipoTributoTexto (idItem) {
  return getTextoLista('LstTipoTributo', idItem) || idItem || '-'
}

function getMomentoGeracaoTexto (idItem) {
  return getTextoLista('LstMomentoGeracao', idItem) || idItem || '-'
}

async function buscarDados (pageNumber = 1) {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) { lista.value = []; return }
  isLoading.value = true
  const params = injetarParametros(pageNumber)
  if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
  if (filtros.lstTipoTributo) params.append('lstTipoTributo', filtros.lstTipoTributo)
  if (filtros.lstMomentoGeracao) params.append('lstMomentoGeracao', filtros.lstMomentoGeracao)
  if (filtros.situacao) params.append('situacao', filtros.situacao)
  if (filtros.dataInicioDe) params.append('dataInicioDe', filtros.dataInicioDe)
  if (filtros.dataInicioAte) params.append('dataInicioAte', filtros.dataInicioAte)
  if (sortField.value) params.append('sortField', sortField.value)
  if (sortOrder.value) params.append('sortOrder', sortOrder.value)

  try {
    const response = await api.get(`TributosConfiguracao?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) { console.error('Erro:', error) } finally { isLoading.value = false }
}

function aplicarFiltros () { buscarDados(1) }
function limparFiltros () {
  filtros.lstTipoTributo = ''
  filtros.lstMomentoGeracao = ''
  filtros.situacao = ''
  filtros.dataInicioDe = ''
  filtros.dataInicioAte = ''
  buscarDados(1)
}
function novoItem () { emit('adicionar-item') }
function editarItem (item) { emit('editar-item', item) }
function abrirAuditoria (item) {
  auditoriaSelecionada.id = item.idTributoConfiguracao
  auditoriaSelecionada.nome = `${getTipoTributoTexto(item.lstTipoTributo)} / ${getMomentoGeracaoTexto(item.lstMomentoGeracao)}`
  mostrarAuditoria.value = true
}

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})
defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Tributos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="podeEditar"
    @mudar-pagina="buscarDados"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="novoItem"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3">
        <div class="col-md-3">
          <label class="form-label small text-body-secondary">Tipo de Tributo</label>
          <select class="form-select" v-model="filtros.lstTipoTributo" @change="aplicarFiltros">
            <option value="">Todos os Tipos</option>
            <option v-for="t in (listas.LstTipoTributo || [])" :key="t.idListaSistemaItem || t.idListaClienteItem" :value="t.idListaSistemaItem || t.idListaClienteItem">{{ t.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small text-body-secondary">Momento de Geração</label>
          <select class="form-select" v-model="filtros.lstMomentoGeracao" @change="aplicarFiltros">
            <option value="">Todos os Momentos</option>
            <option v-for="m in (listas.LstMomentoGeracao || [])" :key="m.idListaSistemaItem || m.idListaClienteItem" :value="m.idListaSistemaItem || m.idListaClienteItem">{{ m.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label small text-body-secondary">Situação</label>
          <select class="form-select" v-model="filtros.situacao" @change="aplicarFiltros">
            <option value="">Todas</option>
            <option value="Vigente">Vigente</option>
            <option value="Expirado">Expirado</option>
            <option value="Futuro">Futuro</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label small text-body-secondary">Início Vigência (de)</label>
          <input type="date" class="form-control" v-model="filtros.dataInicioDe" @change="aplicarFiltros">
        </div>
        <div class="col-md-2">
          <label class="form-label small text-body-secondary">Início Vigência (até)</label>
          <input type="date" class="form-control" v-model="filtros.dataInicioAte" @change="aplicarFiltros">
        </div>
        <div class="col-auto d-flex align-items-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros" :disabled="isLoading">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros" :disabled="isLoading">
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            Aplicar Filtros
          </button>
        </div>
      </div>
    </template>

    <div v-if="isLoading && lista.length === 0" class="text-center p-5"><div class="spinner-border text-primary"></div></div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="isLoading"
      empty-message="Nenhum tributo encontrado para os filtros aplicados."
      row-key="idTributoConfiguracao"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idTributoConfiguracao }}</span>
      </template>

      <template #tipoTributo="{ row }">
        <span :class="obterBadgeNeutro()">{{ getTipoTributoTexto(row.lstTipoTributo) }}</span>
      </template>

      <template #momentoGeracao="{ row }">
        <span :class="obterCorDinamica(row.lstMomentoGeracao, listas.LstMomentoGeracao || [])">{{ getMomentoGeracaoTexto(row.lstMomentoGeracao) }}</span>
      </template>

      <template #dataInicio="{ row }">{{ formatarDataBR(row.dataInicioVigencia) }}</template>
      <template #dataFim="{ row }">{{ formatarDataBR(row.dataFimVigencia) }}</template>

      <template #situacao="{ row }">
        <span :class="obterClasseStatusGhost(row.situacao)">
          {{ row.situacao || '-' }}
        </span>
      </template>

      <template #tsCriacao="{ row }">{{ formatarDataHoraBR(row.tsCriacao) }}</template>
      <template #tsModificacao="{ row }">{{ formatarDataHoraBR(row.tsModificacao) }}</template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico de Alterações">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon text-primary" @click="editarItem(row)" :title="podeEditar ? 'Editar Tributo' : 'Visualizar Tributo'">
            <i :class="podeEditar ? 'bi bi-pencil-fill' : 'bi bi-eye-fill'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="TributoConfiguracao"
    contexto="tenant"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
